import React, { useEffect, useMemo, useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { useAuth } from '@clerk/clerk-react';
import {
  optionLabel,
  normalizeExpectedComposition,
  accessoriesIncluded,
  SHIPMENT_TYPE_OPTIONS,
  MOUNT_TYPE_OPTIONS,
  ACCESSORY_FIELDS
} from '../rmaFieldUtils';
import { colors } from '../theme';
import SubmissionImageGallery from './SubmissionImageGallery';
import RmaCustomerSummary from './RmaCustomerSummary';
import CloseCaseAction from './CloseCaseAction';
import RmaDeviceList from './RmaDeviceList.jsx';

const API_BASE = process.env.REACT_APP_API_URL;

// Shown until the real list arrives, so the dropdown is never empty. The server
// is the source of truth — it also carries the production partners (issue #728).
const FALLBACK_LOCATIONS = [
  { key: 'Haugesund', label: 'Haugesund', kind: 'internal', recipient: 'North Invent AS', address: 'Sørhauggata 128\nN-5527 Haugesund\nNorway' },
  { key: 'Copenhagen', label: 'Copenhagen', kind: 'internal', recipient: 'North Invent AS', address: 'c/o Aalborg University\nA. C. Meyers Vænge 15\n2450 Copenhagen SV\nDenmark' },
];

export default function RmaModalPending({ rma, onClose, onSave }) {
  const [assignedLocation, setAssignedLocation] = useState(
    rma.assignedLocation || 'Haugesund'
  );
  const [locations, setLocations] = useState(FALLBACK_LOCATIONS);
  const { getToken } = useAuth();

  // Which destinations exist is a data question, not a code one: a new repair
  // partner is enabled by ticking a box on its Supplier Matrix record.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/rma/return-locations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!cancelled && data.ok && Array.isArray(data.locations) && data.locations.length) {
          setLocations(data.locations);
        }
      } catch {
        // Keep the fallback — our own sites still work if the lookup fails.
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const selected = locations.find(loc => loc.key === assignedLocation) || locations[0];
  const expectedNormalized = useMemo(
    () => normalizeExpectedComposition(rma.expectedReturn || {}),
    [rma.expectedReturn]
  );
  const expectedMount = expectedNormalized.mounted ? expectedNormalized.mount : null;
  const expectedAccessories = expectedNormalized.accessories || {};
  const expectedHasAccessories = accessoriesIncluded(expectedAccessories);

  const handleConfirm = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          status: 'Assigned',
          assignedLocation
        }),
      });
      const data = await res.json();
      onSave(data);
    } catch (err) {
      console.error('Update failed', err);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h2>Assign RMA</h2>
        <RmaCustomerSummary rma={rma} />
        <p><strong>Serial Number:</strong> {rma.serialNumber}</p>
        <RmaDeviceList rma={rma} title="All units the customer is sending" />
        <p><strong>RMA ID:</strong> {rma.rmaId}</p>

        <label>Assign Location:</label>
        <select
          value={assignedLocation}
          onChange={e => setAssignedLocation(e.target.value)}
          style={styles.input}
        >
          {locations.map(loc => (
            <option key={loc.key} value={loc.key}>
              {loc.kind === 'supplier' ? `${loc.label} (production partner)` : loc.label}
            </option>
        ))}
      </select>
      <p style={{ marginTop: '0.5rem' }}>
        <strong>Address:</strong>{' '}
        {selected ? [selected.recipient, selected.address].filter(Boolean).join(', ').replace(/\n/g, ', ') : ''}
      </p>
      {selected && selected.kind === 'supplier' && (
        <div
          style={{
            marginTop: '0.5rem',
            border: '1px solid rgba(234,179,8,0.3)',
            borderRadius: '10px',
            padding: '0.6rem 0.75rem',
            background: 'rgba(234,179,8,0.06)',
            color: colors.textBody,
            fontSize: '0.85rem',
            textAlign: 'left'
          }}
        >
          The customer ships straight to {selected.label}, skipping a leg through Norway.
          {' '}This is an international return, so it needs a temporary-import permit and a proforma
          invoice — the customer is told to wait for our paperwork before dispatching.
          {selected.contacts && selected.contacts.length > 0
            ? ` ${selected.label} will be notified at ${selected.contacts.map(c => c.email).join(', ')}.`
            : ` No contact is on file for ${selected.label}, so nobody there will be told.`}
          {selected.notes ? ` ${selected.notes}` : ''}
        </div>
      )}

      <div
        style={{
          marginTop: '1rem',
          border: `1px solid ${colors.panelBorder}`,
          borderRadius: '10px',
          padding: '0.75rem',
          backgroundColor: 'rgba(255,255,255,0.04)',
          color: colors.textBody,
          textAlign: 'left'
        }}
      >
        <h3 style={{ margin: '0 0 0.5rem' }}>Expected shipment</h3>
        <p style={{ margin: '0 0 0.25rem' }}>
          <strong>Device Qty:</strong> {expectedNormalized.deviceQuantity || 1} pc
        </p>
        <p style={{ margin: '0 0 0.25rem' }}>
          <strong>Shipment type:</strong>{' '}
          {optionLabel(SHIPMENT_TYPE_OPTIONS, expectedNormalized.shipmentType)}
        </p>
        <p style={{ margin: '0 0 0.25rem' }}>
          <strong>Mounting:</strong>{' '}
          {expectedMount
            ? `${optionLabel(MOUNT_TYPE_OPTIONS, expectedMount.type)} (HMI ${expectedMount.hmi ? 'yes' : 'no'}) x ${expectedMount.qty}`
            : 'Not included'}
        </p>
        <div style={{ margin: '0 0 0.25rem' }}>
          <strong>Accessories:</strong>{' '}
          {expectedHasAccessories ? (
            <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem' }}>
              {ACCESSORY_FIELDS.filter(({ key }) => Number(expectedAccessories[key] || 0) > 0).map(({ key, label }) => (
                <li key={key}>
                  {label} x {expectedAccessories[key]}
                </li>
              ))}
            </ul>
          ) : (
            <span>None specified</span>
          )}
        </div>
      </div>

      {(rma.description || rma.faultType || rma.customerExpectation || rma.warrantyStatus) && (
        <div
          style={{
            marginTop: '1rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '0.75rem',
            backgroundColor: 'rgba(255,255,255,0.05)',
            textAlign: 'left'
          }}
        >
          <h3 style={{ margin: '0 0 0.5rem' }}>Fault & expectations</h3>
          {rma.faultType && (
            <p style={{ margin: '0 0 0.25rem' }}>
              <strong>Fault type:</strong> {rma.faultType}
            </p>
          )}
          {rma.warrantyStatus && (
            <p style={{ margin: '0 0 0.25rem' }}>
              <strong>Warranty status:</strong> {rma.warrantyStatus}
            </p>
          )}
          {rma.customerExpectation && (
            <p style={{ margin: '0 0 0.25rem' }}>
              <strong>Customer expectation:</strong> {rma.customerExpectation}
            </p>
          )}
          {rma.description && (
            <p style={{ margin: '0 0 0.25rem' }}>
              <strong>Description:</strong> {rma.description}
            </p>
          )}
        </div>
      )}

      {rma.customerNotes && rma.customerNotes.length > 0 && (
        <div
          style={{
            marginTop: '1rem',
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '0.75rem',
            backgroundColor: 'rgba(255,255,255,0.05)',
            textAlign: 'left'
          }}
        >
          <h3 style={{ margin: '0 0 0.5rem' }}>Customer communication</h3>
          {rma.customerNotes.map((note, i) => (
            <div
              key={i}
              style={{
                borderTop: i > 0 ? `1px solid ${colors.border}` : 'none',
                paddingTop: i > 0 ? '0.5rem' : 0,
                marginTop: i > 0 ? '0.5rem' : 0
              }}
            >
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.85rem', opacity: 0.7 }}>
                {note.submittedBy} — {new Date(note.createdAt).toLocaleString()}
              </p>
              <p style={{ margin: 0 }}>{note.message}</p>
            </div>
          ))}
        </div>
      )}

      <SubmissionImageGallery images={rma.submissionImages} rmaId={rma._id} canCategorize={true} />

      <div style={{ marginTop: '1rem' }}>
        <button onClick={handleConfirm} style={styles.button}>Confirm</button>
        <button onClick={onClose} style={styles.buttonAlt}>Cancel</button>
      </div>
      <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
        <CloseCaseAction rma={rma} onSave={onSave} />
      </div>
      </div>
    </div>
  );
}

